/* eslint-disable @typescript-eslint/no-explicit-any */
import { i18nWrapper } from 'client/lib/utils';
import * as keyset from 'i18n/DraftControlPanel';

import type { FormErrors } from '../hocs/types';
import { DraftStatus } from '../model/draft';
import { Operation, OperationType } from '../model/operation';
import { AliceApp, Channel, GenericSkill } from '../model/skill';
import { AliceSkillFormSettings, SmartHomeFormSettings } from '../types';

import { labels } from './settings/labels';

const t = i18nWrapper(keyset);

export type ActionButtonTheme = 'normal' | 'action';

type DraftActionType = 'requestReview' | 'cancelReview' | 'requestDeploy';

type ValidationErrors<T extends Channel> = T extends Channel.AliceSkill
    ? FormErrors<AliceSkillFormSettings>
    : T extends Channel.SmartHome
      ? FormErrors<SmartHomeFormSettings>
      : never;

interface ActionButton {
    theme: ActionButtonTheme;
    action: DraftActionType;
    getText: (_isDraftCancelled: boolean) => string;
    settingsShouldBeValid: boolean;
    invalidSettingsMessage: string;
}

const buttons: Record<DraftActionType, ActionButton> = {
    requestReview: {
        theme: 'action',
        action: 'requestReview',
        getText: (isDraftCancelled: boolean) =>
            isDraftCancelled ? t('action-request-review-again') : t('action-request-review'),
        settingsShouldBeValid: true,
        invalidSettingsMessage: '',
    },
    cancelReview: {
        theme: 'normal',
        action: 'cancelReview',
        getText: () => t('action-cancel-review'),
        settingsShouldBeValid: false,
        invalidSettingsMessage: '',
    },
    requestDeploy: {
        theme: 'action',
        action: 'requestDeploy',
        getText: () => t('action-deploy'),
        settingsShouldBeValid: true,
        invalidSettingsMessage: t('invalid-settings'),
    },
};

const commonModerationStatuses = [
    DraftStatus.InDevelopment,
    DraftStatus.ReviewRequested,
    DraftStatus.ReviewApproved,
    DraftStatus.DeployRequested,
];

const skillSelfModerationStatuses = [DraftStatus.InDevelopment, DraftStatus.DeployRequested];
const smartHomeSelfModerationStatuses = [DraftStatus.InDevelopment];

const smartHomeModerationStatuses = [
    DraftStatus.InDevelopment,
    DraftStatus.ReviewRequested,
    DraftStatus.MeetingApproval,
    DraftStatus.WaitingForTesting,
    DraftStatus.DevicesTesting,
    DraftStatus.DevicesTestingCompleted,
    DraftStatus.ReviewApproved,
];

const commonOperationTypeDraftStatusMap = {
    [OperationType.ReviewRequested]: DraftStatus.ReviewRequested,
    [OperationType.ReviewApproved]: DraftStatus.ReviewApproved,
    [OperationType.DeployRequested]: DraftStatus.DeployRequested,
};

const smartHomeOperationTypeDraftStatusMap = {
    [OperationType.ReviewRequested]: DraftStatus.ReviewRequested,
    [OperationType.SettingsReviewApproved]: DraftStatus.MeetingApproval,
    [OperationType.DeviceTestPrepared]: DraftStatus.WaitingForTesting,
    [OperationType.DeviceTestStarted]: DraftStatus.DevicesTesting,
    [OperationType.DeviceTestCompleted]: DraftStatus.DevicesTestingCompleted,
    [OperationType.ReviewApproved]: DraftStatus.DevicesTestingCompleted,
    [OperationType.DeployRequested]: DraftStatus.DeployRequested,
};

const operationTypeDraftStatusMaps: Record<Channel, Record<string, string>> = {
    [Channel.AliceSkill]: commonOperationTypeDraftStatusMap,
    [Channel.SmartHome]: smartHomeOperationTypeDraftStatusMap,
    [Channel.SmartScreenApp]: commonOperationTypeDraftStatusMap,
    [Channel.AutoApp]: commonOperationTypeDraftStatusMap,
    [Channel.TvApp]: commonOperationTypeDraftStatusMap,
};

export const getModerationStatuses = (aliceApp: AliceApp) => {
    if (aliceApp.canSkipReview) {
        return aliceApp.draft.channel === Channel.AliceSkill
            ? skillSelfModerationStatuses
            : smartHomeSelfModerationStatuses;
    }

    return aliceApp.draft.channel === Channel.SmartHome ? smartHomeModerationStatuses : commonModerationStatuses;
};

const commonStatusToActions: Record<DraftStatus, ActionButton[]> = {
    [DraftStatus.InDevelopment]: [buttons.requestReview],
    [DraftStatus.ReviewRequested]: [buttons.cancelReview],
    [DraftStatus.ReviewApproved]: [buttons.requestDeploy, buttons.cancelReview],
    [DraftStatus.DeployRequested]: [],
    [DraftStatus.ReviewCancelled]: [],
    [DraftStatus.ReviewRejected]: [],
    [DraftStatus.DeployRejected]: [],
    [DraftStatus.TestRequired]: [],
    [DraftStatus.MeetingApproval]: [],
    [DraftStatus.WaitingForTesting]: [],
    [DraftStatus.AppScanRequested]: [],
    [DraftStatus.AppScanRequestedAfterModeration]: [],
    [DraftStatus.DevicesTesting]: [],
    [DraftStatus.DevicesTestingCompleted]: [],
};

const selfModerationStatusToActions: Record<DraftStatus, ActionButton[]> = {
    [DraftStatus.InDevelopment]: [buttons.requestDeploy],
    [DraftStatus.ReviewRequested]: [],
    [DraftStatus.ReviewApproved]: [],
    [DraftStatus.DeployRequested]: [],
    [DraftStatus.ReviewCancelled]: [],
    [DraftStatus.ReviewRejected]: [],
    [DraftStatus.DeployRejected]: [],
    [DraftStatus.TestRequired]: [],
    [DraftStatus.MeetingApproval]: [],
    [DraftStatus.WaitingForTesting]: [],
    [DraftStatus.AppScanRequested]: [],
    [DraftStatus.AppScanRequestedAfterModeration]: [],
    [DraftStatus.DevicesTesting]: [],
    [DraftStatus.DevicesTestingCompleted]: [],
};

const smartHomeStatusToActions: Record<DraftStatus, ActionButton[]> = {
    [DraftStatus.InDevelopment]: [buttons.requestReview],
    [DraftStatus.ReviewRequested]: [buttons.cancelReview],
    [DraftStatus.MeetingApproval]: [buttons.cancelReview],
    [DraftStatus.WaitingForTesting]: [buttons.cancelReview],
    [DraftStatus.DevicesTesting]: [buttons.cancelReview],
    [DraftStatus.DevicesTestingCompleted]: [buttons.cancelReview],
    [DraftStatus.ReviewApproved]: [buttons.requestDeploy, buttons.cancelReview],
    [DraftStatus.DeployRequested]: [],
    [DraftStatus.ReviewCancelled]: [],
    [DraftStatus.ReviewRejected]: [],
    [DraftStatus.DeployRejected]: [],
    [DraftStatus.TestRequired]: [],
    [DraftStatus.AppScanRequested]: [],
    [DraftStatus.AppScanRequestedAfterModeration]: [],
};

const smartHomeselfmoderationStatusToActions: Record<DraftStatus, ActionButton[]> = {
    [DraftStatus.InDevelopment]: [buttons.requestDeploy],
    [DraftStatus.ReviewRequested]: [buttons.cancelReview],
    [DraftStatus.MeetingApproval]: [buttons.cancelReview],
    [DraftStatus.WaitingForTesting]: [buttons.cancelReview],
    [DraftStatus.DevicesTesting]: [buttons.cancelReview],
    [DraftStatus.DevicesTestingCompleted]: [buttons.cancelReview],
    [DraftStatus.ReviewApproved]: [buttons.requestDeploy, buttons.cancelReview],
    [DraftStatus.DeployRequested]: [],
    [DraftStatus.ReviewCancelled]: [],
    [DraftStatus.ReviewRejected]: [],
    [DraftStatus.DeployRejected]: [],
    [DraftStatus.TestRequired]: [],
    [DraftStatus.AppScanRequested]: [],
    [DraftStatus.AppScanRequestedAfterModeration]: [],
};

const statusToActions: Record<Channel, Record<DraftStatus, ActionButton[]>> = {
    [Channel.AliceSkill]: commonStatusToActions,
    [Channel.SmartHome]: smartHomeStatusToActions,
    [Channel.TvApp]: commonStatusToActions,
    [Channel.SmartScreenApp]: commonStatusToActions,
    [Channel.AutoApp]: commonStatusToActions,
};

const selfModerationChannelToActions = {
    [Channel.AliceSkill]: selfModerationStatusToActions,
    [Channel.AutoApp]: selfModerationStatusToActions,
    [Channel.SmartHome]: smartHomeselfmoderationStatusToActions,
    [Channel.SmartScreenApp]: selfModerationStatusToActions,
    [Channel.TvApp]: selfModerationStatusToActions,
};

export function getActionButtons<T extends Channel>(skill: GenericSkill<T, any, any>): ActionButton[] {
    const channel = skill.channel;
    const status = skill.draft.status;

    if (skill.canSkipReview) {
        return selfModerationChannelToActions?.[channel]?.[status as DraftStatus] || [];
    }

    return statusToActions[channel][status] || [];
}

export const splitModerationStatusesByCurrentStatus = (moderationStatuses: Array<DraftStatus>, status: DraftStatus) => {
    const currentStatusIndex = moderationStatuses.findIndex((s: string) => s === status);

    const moderationStatusesBeforeCurrent = moderationStatuses.slice(0, currentStatusIndex);
    const moderationStatusesAfterCurrent = moderationStatuses.slice(currentStatusIndex + 1);

    return {
        moderationStatusesBeforeCurrent,
        moderationStatusesAfterCurrent,
    };
};

export const checkIfCancelled = (operations: Operation[], channel: Channel) => {
    const defaultStatus = DraftStatus.InDevelopment;
    const lastOperation = operations[0];
    const secondLastOperation = operations[1];

    const isCancelled = lastOperation.type === OperationType.ReviewCancelled;

    const cancelledOnStatus: DraftStatus = isCancelled
        ? ((operationTypeDraftStatusMaps as any)[channel][secondLastOperation.type] ?? defaultStatus)
        : defaultStatus;

    return isCancelled ? { cancelledOnStatus, cancellationComment: lastOperation.comment } : null;
};

export type ModerationPipelineItemState = 'completed' | 'current' | 'not-started' | 'rejected';

const timingsByStatus: Record<DraftStatus, '' | keyof (typeof keyset)['en']> = {
    [DraftStatus.InDevelopment]: '',
    [DraftStatus.ReviewRequested]: 'timings-1-day',
    [DraftStatus.MeetingApproval]: 'timings-2-weeks',
    [DraftStatus.WaitingForTesting]: '',
    [DraftStatus.DevicesTesting]: 'timings-30-min',
    [DraftStatus.DevicesTestingCompleted]: 'timings-2-weeks',
    [DraftStatus.ReviewApproved]: '',
    [DraftStatus.DeployRequested]: '',
    [DraftStatus.ReviewCancelled]: '',
    [DraftStatus.ReviewRejected]: '',
    [DraftStatus.DeployRejected]: '',
    [DraftStatus.TestRequired]: '',
    [DraftStatus.AppScanRequested]: '',
    [DraftStatus.AppScanRequestedAfterModeration]: '',
};

const extendedTimingsTextByStatus: Record<DraftStatus, '' | keyof (typeof keyset)['en']> = {
    [DraftStatus.InDevelopment]: '',
    [DraftStatus.ReviewRequested]: '',
    [DraftStatus.MeetingApproval]: 'timings-sh-meeting',
    [DraftStatus.WaitingForTesting]: '',
    [DraftStatus.DevicesTesting]: '',
    [DraftStatus.DevicesTestingCompleted]: '',
    [DraftStatus.ReviewApproved]: '',
    [DraftStatus.DeployRequested]: '',
    [DraftStatus.ReviewCancelled]: '',
    [DraftStatus.ReviewRejected]: '',
    [DraftStatus.DeployRejected]: '',
    [DraftStatus.TestRequired]: '',
    [DraftStatus.AppScanRequested]: '',
    [DraftStatus.AppScanRequestedAfterModeration]: '',
};

export const getSlaTimings = <T extends Channel>(
    state: ModerationPipelineItemState,
    channel: T,
    draftStatus: DraftStatus,
) => {
    if (channel === Channel.SmartHome) {
        const timingsText = t(timingsByStatus[draftStatus]);
        const extendedTimingsText = t(extendedTimingsTextByStatus[draftStatus]);

        switch (state) {
            case 'not-started':
                return timingsText;
            case 'current':
                return extendedTimingsText || (timingsText ? `${t('timings-text')} ${timingsText}` : undefined);
            default:
                return undefined;
        }
    }
};

export function makeValidationErrorMessage<C extends Channel>(channel: C, error?: unknown) {
    const header = t('errors-validation') + '\n';

    const l = labels[channel];

    if (typeof error === 'object' && error && 'fields' in error && !!error.fields) {
        const errorField = error?.fields || {};
        return (
            header +
            Object.entries(errorField as ValidationErrors<C>)
                .map(([k, v]) => {
                    const label = k in l ? (l as any)[k] : t('others');
                    const value = Array.isArray(v) ? v.filter(Boolean)[0] : v;

                    return `— ${label}: ${value}`;
                })
                .join('\n')
        );
    }
    return t('error-try-again');
}
